
import type { ApiErrorBody, ApiErrorCode, ValidationFieldError } from '../types/api';
import { CSRF_COOKIE } from '../types/api';

const API_PREFIX = '/api';

const DEFAULT_TIMEOUT_MS = 30_000;

const CSRF_HEADER = 'X-CSRF-Token';

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export class ApiError extends Error {
  readonly status: number;
  readonly errorId: string | null;
  readonly code: ApiErrorCode | null;
  readonly details: Record<string, unknown> | null;
  readonly retryAfterSeconds: number | null;

  constructor(
    status: number,
    body: Partial<ApiErrorBody>,
    fallbackMessage: string,
    retryAfterSeconds: number | null = null,
  ) {
    super(body.message || fallbackMessage);
    this.name = 'ApiError';
    this.status = status;
    this.errorId = body.error_id ?? null;
    this.code = body.code ?? null;
    this.details = body.details ?? null;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }

  get fieldErrors(): ValidationFieldError[] {
    const fields = this.details?.fields;
    return Array.isArray(fields) ? (fields as ValidationFieldError[]) : [];
  }

  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
}

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

let lastUnauthorizedAt = 0;

function notifyUnauthorized(): void {
  const now = Date.now();
  if (now - lastUnauthorizedAt < 1000) return;
  lastUnauthorizedAt = now;
  setCsrfToken(null);
  unauthorizedHandler?.();
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const raw of document.cookie.split('; ')) {
    if (raw.startsWith(prefix)) return decodeURIComponent(raw.slice(prefix.length));
  }
  return null;
}

function parseRetryAfter(response: Response): number | null {
  const raw = response.headers.get('retry-after');
  if (!raw) return null;
  const seconds = Number.parseInt(raw, 10);
  return Number.isFinite(seconds) ? seconds : null;
}

async function parseErrorBody(response: Response): Promise<Partial<ApiErrorBody>> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return {};
  }
  try {
    const parsed: unknown = await response.json();
    if (!parsed || typeof parsed !== 'object') return {};
    const obj = parsed as Record<string, unknown>;

    if (typeof obj.error === 'object' && obj.error !== null) {
      return obj.error as Partial<ApiErrorBody>;
    }
    if (typeof obj.detail === 'string') {
      return { message: obj.detail };
    }
    return {};
  } catch {
    return {};
  }
}

export interface RequestOptions {
  json?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined | null | (string | number)[]>;
  signal?: AbortSignal;
  timeoutMs?: number;
  headers?: Record<string, string>;
  skipAuthRedirect?: boolean;
  acceptStatuses?: readonly number[];
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${API_PREFIX}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const {
    json,
    formData,
    query,
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    headers = {},
    skipAuthRedirect = false,
    acceptStatuses = [],
  } = options;

  if (json !== undefined && formData !== undefined) {
    throw new Error('request(): передайте либо json, либо formData, но не оба');
  }

  const requestHeaders = new Headers(headers);
  requestHeaders.set('Accept', 'application/json');

  let body: BodyInit | undefined;
  if (formData !== undefined) {
    body = formData;
  } else if (json !== undefined) {
    requestHeaders.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  }

  if (MUTATING_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE) ?? csrfToken;
    if (csrf) requestHeaders.set(CSRF_HEADER, csrf);
  }

  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      credentials: 'include',
      headers: requestHeaders,
      body,
      signal: combinedSignal,
    });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    const message = timeoutSignal.aborted
      ? `Запрос превысил таймаут ${timeoutMs} мс`
      : 'Сеть недоступна или сервер не отвечает';
    throw new ApiError(0, { message }, message);
  }

  if (response.status === 401) {
    if (!skipAuthRedirect) notifyUnauthorized();
    throw new ApiError(401, await parseErrorBody(response), 'Требуется вход');
  }

  if (!response.ok && !acceptStatuses.includes(response.status)) {
    throw new ApiError(
      response.status,
      await parseErrorBody(response),
      `Ошибка ${response.status} ${response.statusText}`,
      parseRetryAfter(response),
    );
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as unknown as T;
  }

  return (await response.json()) as T;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  fraction: number;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  timeoutMs?: number;
  signal?: AbortSignal;
}

const UPLOAD_TIMEOUT_MS = 30 * 60_000;

export function uploadWithProgress<T>(
  url: string,
  formData: FormData,
  options: UploadOptions = {},
): Promise<T> {
  const { onProgress, timeoutMs = UPLOAD_TIMEOUT_MS, signal } = options;

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    xhr.timeout = timeoutMs;
    xhr.setRequestHeader('Accept', 'application/json');
    const csrf = readCookie(CSRF_COOKIE) ?? csrfToken;
    if (csrf) xhr.setRequestHeader(CSRF_HEADER, csrf);

    xhr.upload.onprogress = (event) => {
      onProgress?.({
        loaded: event.loaded,
        total: event.total,
        fraction: event.lengthComputable && event.total > 0 ? event.loaded / event.total : 0,
      });
    };

    const parseBody = (): { data: unknown; error: Partial<ApiErrorBody> } => {
      try {
        const parsed: unknown = JSON.parse(xhr.responseText || 'null');
        const obj = parsed as Record<string, unknown> | null;
        if (obj && typeof obj.error === 'object' && obj.error !== null) {
          return { data: parsed, error: obj.error as Partial<ApiErrorBody> };
        }
        return { data: parsed, error: {} };
      } catch {
        return { data: null, error: {} };
      }
    };

    xhr.onload = () => {
      const { data, error } = parseBody();
      if (xhr.status === 401) {
        notifyUnauthorized();
        reject(new ApiError(401, error, 'Требуется вход'));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(xhr.status, error, `Ошибка ${xhr.status} ${xhr.statusText}`));
        return;
      }
      resolve(data as T);
    };
    xhr.onerror = () => {
      const message = 'Сеть недоступна или сервер не отвечает';
      reject(new ApiError(0, { message }, message));
    };
    xhr.ontimeout = () => {
      const message = `Загрузка превысила таймаут ${Math.round(timeoutMs / 1000)} с`;
      reject(new ApiError(0, { message }, message));
    };
    xhr.onabort = () => reject(new DOMException('Загрузка отменена', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });

    xhr.send(formData);
  });
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
  post: <T>(path: string, options?: RequestOptions) => request<T>('POST', path, options),
  put: <T>(path: string, options?: RequestOptions) => request<T>('PUT', path, options),
  patch: <T>(path: string, options?: RequestOptions) => request<T>('PATCH', path, options),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
};

export function describeError(error: unknown): { message: string; errorId: string | null } {
  if (error instanceof ApiError) {
    return { message: error.message, errorId: error.errorId };
  }
  if (error instanceof Error) {
    return { message: error.message, errorId: null };
  }
  return { message: 'Неизвестная ошибка', errorId: null };
}
